import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const confidenceLevels = [
  { emoji: "😟", label: "Not confident yet", value: 1 },
  { emoji: "😐", label: "Getting there", value: 2 },
  { emoji: "🙂", label: "Confident", value: 3 },
  { emoji: "🚀", label: "Super confident", value: 4 }
];

function EducatorAssessmentDashboard({ user, onLogout }) {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [classData, setClassData] = useState(null);
  const [students, setStudents] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [modules, setModules] = useState([]);
  const [studentProgress, setStudentProgress] = useState({});
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedSession, setSelectedSession] = useState("all");
  const [viewMode, setViewMode] = useState("overview"); // overview, reflections, confidence

  useEffect(() => {
    loadData();
  }, [classId]);

  const loadData = async () => {
    try {
      const [classRes, assessmentsRes] = await Promise.all([
        axios.get(`${API}/classes/${classId}`),
        axios.get(`${API}/classes/${classId}/assessments`)
      ]);
      
      setClassData(classRes.data.class);
      setStudents(assessmentsRes.data.students || []);
      setAssessments(assessmentsRes.data.assessments || []);
      setModules(assessmentsRes.data.modules || []);
      setStudentProgress(assessmentsRes.data.student_progress || {});
    } catch (err) {
      console.error("Error loading assessment data:", err);
    } finally {
      setLoading(false);
    }
  };

  const getConfidenceEmoji = (level) => {
    const conf = confidenceLevels.find(c => c.value === level);
    return conf ? conf.emoji : "❓";
  };

  const getConfidenceLabel = (level) => {
    const conf = confidenceLevels.find(c => c.value === level);
    return conf ? conf.label : "Unknown";
  };

  const getStudentAssessments = (studentId) => {
    return assessments.filter(a => a.user_id === studentId);
  };

  const getSessionAssessments = (sessionNumber) => {
    return assessments.filter(a => a.session_number === sessionNumber);
  };

  const filteredAssessments = () => {
    let filtered = [...assessments];
    if (selectedStudent) {
      filtered = filtered.filter(a => a.user_id === selectedStudent);
    }
    if (selectedSession !== "all") {
      filtered = filtered.filter(a => a.session_number === parseInt(selectedSession));
    }
    return filtered.sort((a, b) => a.session_order - b.session_order);
  };

  const calculateAverageConfidence = (sessionAssessments) => {
    if (sessionAssessments.length === 0) return 0;
    const sum = sessionAssessments.reduce((acc, a) => acc + (a.confidence_level || 0), 0);
    return (sum / sessionAssessments.length).toFixed(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-bounce">🍋</div>
          <p className="text-gray-600">Loading assessment data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/classes/${classId}`)}
            className="text-blue-600 hover:text-blue-800 mb-4 flex items-center gap-2"
          >
            ← Back to Class
          </button>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            📊 Assessment Dashboard
          </h1>
          <p className="text-gray-600">
            {classData?.name} • View student reflections and confidence levels
          </p>
        </div>

        {/* View Mode Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: "overview", label: "📈 Overview", icon: "📈" },
            { id: "reflections", label: "💭 Reflections", icon: "💭" },
            { id: "confidence", label: "💪 Confidence Trends", icon: "💪" }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setViewMode(tab.id)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                viewMode === tab.id
                  ? "bg-blue-500 text-white shadow-lg"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Student</label>
              <select
                value={selectedStudent || ""}
                onChange={(e) => setSelectedStudent(e.target.value || null)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Students</option>
                {students.map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Session</label>
              <select
                value={selectedSession}
                onChange={(e) => setSelectedSession(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Sessions</option>
                {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                  <option key={num} value={num}>
                    Session {num}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Overview View */}
        {viewMode === "overview" && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="text-3xl mb-2">👥</div>
                <div className="text-3xl font-bold text-gray-800">{students.length}</div>
                <div className="text-gray-600">Total Students</div>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="text-3xl mb-2">✅</div>
                <div className="text-3xl font-bold text-green-600">{assessments.length}</div>
                <div className="text-gray-600">Assessments Completed</div>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="text-3xl mb-2">💪</div>
                <div className="text-3xl font-bold text-purple-600">
                  {calculateAverageConfidence(assessments)}
                </div>
                <div className="text-gray-600">Avg. Confidence (1-4)</div>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="text-3xl mb-2">📝</div>
                <div className="text-3xl font-bold text-blue-600">
                  {assessments.filter(a => a.reflections?.r1).length}
                </div>
                <div className="text-gray-600">Reflections Submitted</div>
              </div>
            </div>

            {/* Student Progress Table */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold text-gray-800">📋 Student Completion Status</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Student</th>
                      {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                        <th key={num} className="px-3 py-3 text-center text-sm font-semibold text-gray-700">
                          S{num}
                        </th>
                      ))}
                      <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {students.map((student) => {
                      const progress = studentProgress[student.id] || { completed_sessions: [], total_completed: 0 };
                      return (
                        <tr key={student.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-800">{student.name}</div>
                            <div className="text-sm text-gray-500">{student.email}</div>
                          </td>
                          {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                            <td key={num} className="px-3 py-4 text-center">
                              {progress.completed_sessions.includes(num) ? (
                                <span className="text-green-500 text-xl">✓</span>
                              ) : (
                                <span className="text-gray-300 text-xl">○</span>
                              )}
                            </td>
                          ))}
                          <td className="px-6 py-4 text-center">
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                              progress.total_completed === 7
                                ? "bg-green-100 text-green-700"
                                : progress.total_completed > 0
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-gray-100 text-gray-500"
                            }`}>
                              {progress.total_completed}/7
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Reflections View */}
        {viewMode === "reflections" && (
          <div className="space-y-6">
            {filteredAssessments().length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon" aria-hidden="true">📝</div>
                <h3 className="empty-state-title">No Reflections Yet</h3>
                <p className="empty-state-description">Student reflections will appear here once they complete their session assessments. Check back soon!</p>
              </div>
            ) : (
              filteredAssessments().map((assessment, index) => {
                const student = students.find(s => s.id === assessment.user_id);
                return (
                  <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-4">
                      <div className="flex justify-between items-center text-white">
                        <div>
                          <h3 className="font-bold text-lg">{student?.name || "Unknown Student"}</h3>
                          <p className="text-blue-100">{assessment.session_title}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl">{getConfidenceEmoji(assessment.confidence_level)}</div>
                          <p className="text-sm text-blue-100">{getConfidenceLabel(assessment.confidence_level)}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-6 space-y-4">
                      {assessment.reflections && (
                        <>
                          {assessment.reflections.r1 && (
                            <div className="bg-gray-50 rounded-lg p-4">
                              <p className="text-sm font-semibold text-gray-500 mb-2">Reflection 1</p>
                              <p className="text-gray-800">{assessment.reflections.r1}</p>
                            </div>
                          )}
                          {assessment.reflections.r2 && (
                            <div className="bg-gray-50 rounded-lg p-4">
                              <p className="text-sm font-semibold text-gray-500 mb-2">Reflection 2</p>
                              <p className="text-gray-800">{assessment.reflections.r2}</p>
                            </div>
                          )}
                          {assessment.reflections.r3 && (
                            <div className="bg-gray-50 rounded-lg p-4">
                              <p className="text-sm font-semibold text-gray-500 mb-2">Reflection 3</p>
                              <p className="text-gray-800">{assessment.reflections.r3}</p>
                            </div>
                          )}
                        </>
                      )}
                      
                      <div className="pt-4 border-t flex justify-between items-center text-sm text-gray-500">
                        <span>Submitted: {new Date(assessment.submitted_at).toLocaleDateString()}</span>
                        <span>Session {assessment.session_number}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Confidence Trends View */}
        {viewMode === "confidence" && (
          <div className="space-y-6">
            {/* Session Confidence Summary */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-6">📊 Confidence by Session</h2>
              <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
                {[1, 2, 3, 4, 5, 6, 7].map((sessionNum) => {
                  const sessionAssessments = getSessionAssessments(sessionNum);
                  const avgConfidence = calculateAverageConfidence(sessionAssessments);
                  const confidenceLevel = Math.round(avgConfidence);
                  
                  return (
                    <div key={sessionNum} className="text-center p-4 bg-gray-50 rounded-xl">
                      <div className="text-sm font-semibold text-gray-500 mb-2">Session {sessionNum}</div>
                      <div className="text-4xl mb-2">{getConfidenceEmoji(confidenceLevel) || "—"}</div>
                      <div className="text-xl font-bold text-gray-800">{avgConfidence || "—"}</div>
                      <div className="text-xs text-gray-500">{sessionAssessments.length} responses</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Individual Student Confidence */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold text-gray-800">👤 Student Confidence Journey</h2>
                <p className="text-gray-600 text-sm">Track how each student&apos;s confidence grows throughout the program</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Student</th>
                      {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                        <th key={num} className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                          S{num}
                        </th>
                      ))}
                      <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">Growth</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {students.map((student) => {
                      const studentAssessments = getStudentAssessments(student.id);
                      const confidenceBySession = {};
                      studentAssessments.forEach(a => {
                        confidenceBySession[a.session_number] = a.confidence_level;
                      });
                      
                      // Calculate growth (last - first completed session)
                      const completedSessions = Object.keys(confidenceBySession).map(Number).sort((a, b) => a - b);
                      let growth = null;
                      if (completedSessions.length >= 2) {
                        const first = confidenceBySession[completedSessions[0]];
                        const last = confidenceBySession[completedSessions[completedSessions.length - 1]];
                        growth = last - first;
                      }
                      
                      return (
                        <tr key={student.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-800">{student.name}</div>
                          </td>
                          {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                            <td key={num} className="px-4 py-4 text-center">
                              {confidenceBySession[num] ? (
                                <span className="text-2xl">{getConfidenceEmoji(confidenceBySession[num])}</span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                          ))}
                          <td className="px-6 py-4 text-center">
                            {growth !== null ? (
                              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                growth > 0
                                  ? "bg-green-100 text-green-700"
                                  : growth < 0
                                  ? "bg-red-100 text-red-700"
                                  : "bg-gray-100 text-gray-600"
                              }`}>
                                {growth > 0 ? `+${growth}` : growth}
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Confidence Legend */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">📖 Confidence Level Legend</h3>
              <div className="flex flex-wrap gap-6">
                {confidenceLevels.map((level) => (
                  <div key={level.value} className="flex items-center gap-2">
                    <span className="text-2xl">{level.emoji}</span>
                    <span className="text-gray-700">{level.value} = {level.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EducatorAssessmentDashboard;
